import {
  flattenMatchedTagNames,
  formatAssessmentDateTime,
  formatAssessmentStatusLabel,
  formatObjectSideCount,
  formatScoreText,
  mapBundleStatusToUi,
  mapRelevanceLevel,
  objectSideLabel,
  sideTypeDisplayName,
  type OtoAssessmentListItem,
  type OtoAssessmentResults,
  type OtoRelevanceLevel,
  type OtoRelevanceThresholds,
  type OtoResultsGroup,
} from "../../../services/objectToObjectAssessmentService";

export type ObjectAnalysisEntity = {
  key: string;
  name: string;
  code: string;
  version: string;
};

export type ObjectAnalysisGroupRow = {
  id: string;
  rank: number;
  target: ObjectAnalysisEntity;
  status: string;
  statusTone: "completed" | "failed" | "partial" | "inProgress";
  errorMessage: string | null;
  matchingTags: string[];
  relevanceScore: string;
  relevance: ObjectAnalysisRelevance | null;
};

export type ObjectAnalysisRelevance = OtoRelevanceLevel;

export type ObjectAnalysisGroup = {
  key: string;
  source: ObjectAnalysisEntity;
  rows: ObjectAnalysisGroupRow[];
};

export type ObjectAnalysisDirectionOption = {
  objectType: string;
  label: string;
  targetLabel: string;
};

export type ObjectAnalysisAssessmentDetail = {
  assessmentId: string;
  status: string;
  statusTone: "completed" | "failed" | "partial" | "inProgress";
  /** First pair-level error_message when the bundle (or a pair) failed. */
  errorMessage: string | null;
  assessmentTime: string;
  objectAType: string;
  objectBType: string;
  objectASubType: string;
  objectBSubType: string;
  objectACount: number;
  objectBCount: number;
  objectALabel: string;
  objectBLabel: string;
  directions: ObjectAnalysisDirectionOption[];
  groups: ObjectAnalysisGroup[];
  groupbyObjectType: string;
};

function versionLabel(version: string | null | undefined): string {
  if (!version?.trim()) return "-";
  const trimmed = version.trim();
  return trimmed.toUpperCase().startsWith("V") ? trimmed : `V${trimmed}`;
}

/** First letter uppercase; leave the rest unchanged. */
export function capitalizeFirstLetter(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

/** `object_id · object_version` as plain text. */
export function formatObjectEntityDetails(
  objectId: string,
  version: string,
): string {
  return [objectId, version].join(" · ");
}

function toEntity(args: {
  objectId: string;
  objectTitle: string | null;
  objectVersion: string | null;
}): ObjectAnalysisEntity {
  const title = args.objectTitle?.trim();
  return {
    key: args.objectId,
    name: title ? capitalizeFirstLetter(title) : "-",
    code: args.objectId,
    version: versionLabel(args.objectVersion),
  };
}

function pairStatusLabel(status: string): string {
  const normalized = status.trim().toLowerCase();
  if (normalized === "success") return "Completed";
  if (normalized === "pending") return "Pending";
  if (normalized === "tagging" || normalized === "scoring") return "In Progress";
  if (normalized.startsWith("failed")) return "Failed";
  return status || "-";
}

function pairStatusTone(
  status: string,
): "completed" | "failed" | "partial" | "inProgress" {
  const normalized = status.trim().toLowerCase();
  if (normalized === "success") return "completed";
  if (normalized.startsWith("failed")) return "failed";
  if (normalized === "partial") return "partial";
  return "inProgress";
}

function bundleStatusLabel(
  status: string,
  completedCount: number,
  totalPairs: number,
): string {
  return formatAssessmentStatusLabel(status, completedCount, totalPairs);
}

function countUniqueAnchors(groups: OtoResultsGroup[]): number {
  return new Set(groups.map((group) => group.objectId)).size;
}

function countUniqueTargets(groups: OtoResultsGroup[]): number {
  const ids = new Set<string>();
  for (const group of groups) {
    for (const pair of group.pairs) {
      ids.add(pair.objectId);
    }
  }
  return ids.size;
}

function buildDirections(
  objectAType: string,
  objectBType: string,
  objectASubType: string,
  objectBSubType: string,
): ObjectAnalysisDirectionOption[] {
  const a = objectAType.trim();
  const b = objectBType.trim();
  if (!a && !b) return [];
  if (a && b && a === b) {
    const label = sideTypeDisplayName(a);
    return [
      {
        objectType: a,
        label: `${label} to ${label}`,
        targetLabel: objectSideLabel(a, objectBSubType),
      },
    ];
  }

  const options: ObjectAnalysisDirectionOption[] = [];
  if (a) {
    options.push({
      objectType: a,
      label: `${sideTypeDisplayName(a)} to ${sideTypeDisplayName(b || a)}`,
      targetLabel: objectSideLabel(b || a, objectBSubType),
    });
  }
  if (b && b !== a) {
    options.push({
      objectType: b,
      label: `${sideTypeDisplayName(b)} to ${sideTypeDisplayName(a || b)}`,
      targetLabel: objectSideLabel(a || b, objectASubType),
    });
  }
  return options;
}

export function mapResultsToDetail(
  results: OtoAssessmentResults,
  thresholds: OtoRelevanceThresholds,
  createdAt?: string | null,
  completedCount = 0,
  assessment?: OtoAssessmentListItem | null,
): ObjectAnalysisAssessmentDetail {
  const anchorGroup = results.groups[0];
  const anchorSubType = anchorGroup?.objectSubType || "";
  const targetSubType = anchorGroup?.pairs[0]?.objectSubType || "";

  // Directions always come from assessment-by-id object_a / object_b.
  const objectAType =
    assessment?.objectA.objectType ||
    results.objectA?.objectType ||
    results.objectAType ||
    results.groupbyObjectType;
  const objectBType =
    assessment?.objectB.objectType ||
    results.objectB?.objectType ||
    results.objectBType ||
    results.groups[0]?.pairs[0]?.objectType ||
    "";
  const sideA = assessment?.objectA ?? results.objectA;
  const sideB = assessment?.objectB ?? results.objectB;

  const groups: ObjectAnalysisGroup[] = results.groups.map((group) => ({
    key: group.objectId,
    source: toEntity({
      objectId: group.objectId,
      objectTitle: group.objectTitle,
      objectVersion: group.objectVersion,
    }),
    rows: group.pairs.map((pair, index) => ({
      id: pair.uuid,
      rank: index + 1,
      target: toEntity({
        objectId: pair.objectId,
        objectTitle: pair.objectTitle,
        objectVersion: pair.objectVersion,
      }),
      status: pairStatusLabel(pair.status),
      statusTone: pairStatusTone(pair.status),
      errorMessage: pair.errorMessage,
      matchingTags: flattenMatchedTagNames(pair.matchedTags),
      relevanceScore: formatScoreText(pair.relevanceScore, pair.totalWeight),
      relevance: mapRelevanceLevel(
        pair.relevanceScore,
        pair.totalWeight,
        thresholds,
      ),
    })),
  }));

  const anchorCount = countUniqueAnchors(results.groups);
  const targetCount = countUniqueTargets(results.groups);
  const groupbyIsA =
    !objectAType ||
    results.groupbyObjectType === objectAType ||
    !objectBType;
  const firstPairError =
    results.groups
      .flatMap((group) => group.pairs)
      .map((pair) => pair.errorMessage?.trim() || "")
      .find((message) => Boolean(message)) || null;

  return {
    assessmentId: results.taskId,
    status: bundleStatusLabel(
      results.status,
      completedCount,
      results.totalPairs,
    ),
    statusTone: mapBundleStatusToUi(results.status, completedCount),
    errorMessage: firstPairError,
    assessmentTime: formatAssessmentDateTime(
      results.createdAt ?? createdAt ?? assessment?.createdAt ?? null,
    ),
    objectAType,
    objectBType,
    objectASubType:
      sideA?.objectSubType || (groupbyIsA ? anchorSubType : targetSubType),
    objectBSubType:
      sideB?.objectSubType || (groupbyIsA ? targetSubType : anchorSubType),
    objectACount: sideA?.count || (groupbyIsA ? anchorCount : targetCount),
    objectBCount: sideB?.count || (groupbyIsA ? targetCount : anchorCount),
    objectALabel: sideTypeDisplayName(objectAType),
    objectBLabel: sideTypeDisplayName(objectBType),
    directions: buildDirections(
      objectAType,
      objectBType,
      sideA?.objectSubType || "",
      sideB?.objectSubType || "",
    ),
    groups,
    groupbyObjectType: results.groupbyObjectType,
  };
}

export function formatDetailSummaryLine(
  detail: ObjectAnalysisAssessmentDetail,
): string {
  const groupbyIsA =
    !detail.objectAType ||
    detail.groupbyObjectType === detail.objectAType ||
    !detail.objectBType;

  const fromLabel = groupbyIsA ? detail.objectALabel : detail.objectBLabel;
  const toLabel = groupbyIsA ? detail.objectBLabel : detail.objectALabel;
  const fromCount = groupbyIsA ? detail.objectACount : detail.objectBCount;
  const toCount = groupbyIsA ? detail.objectBCount : detail.objectACount;
  const fromType = groupbyIsA ? detail.objectAType : detail.objectBType;
  const toType = groupbyIsA ? detail.objectBType : detail.objectAType;
  const fromSubType = groupbyIsA
    ? detail.objectASubType
    : detail.objectBSubType;
  const toSubType = groupbyIsA ? detail.objectBSubType : detail.objectASubType;

  const arrow = `${fromLabel} → ${toLabel}`;
  const fromUnit = formatObjectSideCount(fromCount, fromType, fromSubType);
  const toUnit = formatObjectSideCount(toCount, toType, toSubType);

  return [arrow, detail.assessmentTime, fromUnit, toUnit].join(" · ");
}
